// Eine Animation je Aktion: die Maßnahme wird in der Welt sichtbar umgesetzt.
import * as THREE from "three"
import gsap from "gsap"
import { COLORS } from "../world/palette.js"
import { at, box, cylinder, solarPanel, windTurbine, flag, arrowRing, bike, mat } from "../world/builders.js"
import { STATION_LAYOUT } from "../world/stations.js"
import { buildRailway } from "../world/rail.js"

function spin(world, object, speed, axis = "y") {
  world.extras.push({ update: (dt) => (object.rotation[axis] += speed * dt) })
}

function dropIn(object, { height = 12, delay = 0, duration = 0.9 } = {}) {
  const y = object.position.y
  object.position.y = y + height
  gsap.to(object.position, { y, duration, delay, ease: "bounce.out" })
}

function popIn(object, { delay = 0, duration = 0.8 } = {}) {
  const s = object.scale.x || 1
  object.scale.setScalar(0.001)
  gsap.to(object.scale, { x: s, y: s, z: s, duration, delay, ease: "back.out(2)" })
}

function cleanSmoke(world, id, amount = 1) {
  const station = world.stations[id]
  if (!station) return
  const color = new THREE.Color(COLORS.smokeDirty).lerp(new THREE.Color(COLORS.smokeClean), amount).getHex()
  station.emitters.forEach((emitter) => (emitter.color = color))
}

function stationGroup(world, id) {
  return world.stations[id]?.group
}

function lightningBolt() {
  const shape = new THREE.Shape()
  shape.moveTo(0, 0.9)
  shape.lineTo(0.35, 0.9)
  shape.lineTo(0.15, 0.45)
  shape.lineTo(0.45, 0.45)
  shape.lineTo(-0.1, -0.4)
  shape.lineTo(0.05, 0.15)
  shape.lineTo(-0.2, 0.15)
  shape.closePath()
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.12, bevelEnabled: false })
  return new THREE.Mesh(geometry, mat(COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.6 }))
}

export const ANIMATIONS = {
  scanner(world) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1, 0.35, 6, 64),
      new THREE.MeshBasicMaterial({ color: COLORS.lime, transparent: true, opacity: 0.9 })
    )
    ring.rotation.x = Math.PI / 2
    ring.position.y = 1
    const wall = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 1, 14, 64, 1, true),
      new THREE.MeshBasicMaterial({ color: COLORS.lime, transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false })
    )
    wall.position.y = 7
    world.root.add(ring, wall)
    const state = { r: 1 }
    return new Promise((resolve) => {
      gsap.to(state, {
        r: 72, duration: 3.2, ease: "power1.inOut",
        onUpdate: () => {
          ring.scale.set(state.r, state.r, 1)
          wall.scale.set(state.r, 1, state.r)
          ring.material.opacity = 0.9 * (1 - state.r / 90)
          wall.material.opacity = 0.18 * (1 - state.r / 90)
        },
        onComplete: () => {
          world.root.remove(ring, wall)
          resolve()
        }
      })
    })
  },

  lights(world) {
    const factory = stationGroup(world, "factory")
    for (const roof of factory.userData.roofs) {
      const strip = at(box(roof.w * 0.8, 0.12, 0.3, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 1 }), 0, roof.y + 0.1, 0)
      roof.object.add(strip)
      popIn(strip)
    }
    factory.userData.halls.forEach((h, i) => gsap.fromTo(h.scale, { y: 1 }, { y: 1.06, yoyo: true, repeat: 3, duration: 0.18, delay: i * 0.1 }))
  },

  solar(world) {
    const factory = stationGroup(world, "factory")
    let delay = 0
    for (const roof of factory.userData.roofs) {
      const cols = Math.max(2, Math.floor(roof.w / 1.3))
      const rows = Math.max(1, Math.floor(roof.d / 1.1))
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          const panel = solarPanel()
          panel.position.set(-roof.w / 2 + 0.7 + c * ((roof.w - 1.4) / Math.max(1, cols - 1)), roof.y + 0.15, -roof.d / 2 + 0.6 + r * ((roof.d - 1.2) / Math.max(1, rows - 1)))
          roof.object.add(panel)
          dropIn(panel, { height: 10, delay, duration: 0.7 })
          delay += 0.03
        }
      }
    }
  },

  wind(world) {
    const plant = stationGroup(world, "power_plant")
    const spots = plant?.userData.windSpots || [[8, -3], [11, 1], [8, 4.5]]
    spots.forEach(([x, z], i) => {
      const turbine = at(windTurbine(10), x, 0, z, Math.PI / 2)
      ;(plant || world.dynamic).add(turbine)
      turbine.scale.set(1, 0.001, 1)
      gsap.to(turbine.scale, { y: 1, duration: 1.4, delay: i * 0.35, ease: "elastic.out(1, 0.5)" })
      spin(world, turbine.userData.rotor, 2.2 + i * 0.3, "x")
    })
    if (world.powerLine) {
      world.powerLine.material.color.set(COLORS.green)
      gsap.fromTo(world.powerLine.material, { emissiveIntensity: 1.2 }, { emissiveIntensity: 0.35, duration: 1.2, repeat: 2, yoyo: true })
    }
    cleanSmoke(world, "power_plant", 0.7)
  },

  heatpump(world) {
    const factory = stationGroup(world, "factory")
    const boiler = factory.userData.boiler
    if (boiler) {
      const { x, z } = boiler.position
      gsap.to(boiler.position, { x: x - 4, duration: 1.1, ease: "power2.in" })
      gsap.to(boiler.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.5, delay: 0.9, onComplete: () => (boiler.visible = false) })
      const pump = at(box(1.6, 1.4, 1.2, COLORS.white), x, 0, z)
      const fan = at(cylinder(0.5, 0.5, 0.1, COLORS.steel, 10), 0, 1.45, 0)
      pump.add(fan)
      pump.add(at(box(1.62, 0.15, 1.22, COLORS.green), 0, 0.2, 0))
      factory.add(pump)
      dropIn(pump, { height: 14, delay: 1.2 })
      spin(world, fan, 6)
    }
    cleanSmoke(world, "factory", 0.8)
  },

  efleet(world) {
    const factory = stationGroup(world, "factory")
    for (const [i, vehicle] of (factory.userData.fleet || []).entries()) {
      const bolt = lightningBolt()
      bolt.position.set(0.3, 2.3, 0)
      bolt.scale.setScalar(1.3)
      vehicle.add(bolt)
      popIn(bolt, { delay: i * 0.2 })
      spin(world, bolt, 1.5)
      gsap.to(vehicle.position, { y: 0.6, yoyo: true, repeat: 1, duration: 0.25, delay: i * 0.2 })
    }
    world.moversTagged("trucks").slice(0, 3).forEach((mover) => {
      const bolt = lightningBolt()
      bolt.position.set(0, 2.2, 0)
      mover.object.add(bolt)
      popIn(bolt)
    })
  },

  flags(world) {
    const ids = ["component_works", "steel_mill", "chem_plant", "farm", "quarry", "office_park"].filter((id) => world.stations[id])
    ids.forEach((id, i) => {
      const g = stationGroup(world, id)
      const spots = g.userData.flagSpots || [[-4.5, 4], [4.5, 4]]
      spots.forEach(([x, z], j) => {
        const f = at(flag(COLORS.green), x, 0, z)
        g.add(f)
        f.scale.set(1, 0.001, 1)
        gsap.to(f.scale, { y: 1, duration: 0.8, delay: i * 0.2 + j * 0.1, ease: "back.out(2)" })
        world.extras.push({ update: (dt, time) => (f.userData.cloth.rotation.y = Math.sin(time * 4 + j) * 0.25) })
      })
    })
  },

  assortment(world) {
    const g = stationGroup(world, "component_works") || stationGroup(world, "factory")
    for (let i = 0; i < 5; i++) {
      const tag = at(box(0.5, 0.7, 0.08, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.4 }), -3 + i * 1.5, 3.8, 4.5)
      g.add(tag)
      popIn(tag, { delay: i * 0.12 })
      spin(world, tag, 1.2)
    }
  },

  greensteel(world) {
    const g = stationGroup(world, "steel_mill")
    if (!g) return
    cleanSmoke(world, "steel_mill", 0.9)
    if (g.userData.glow) {
      g.userData.glow.material = mat(COLORS.green, { emissive: COLORS.green, emissiveIntensity: 1 })
      gsap.fromTo(g.userData.glow.scale, { x: 1, y: 1 }, { x: 1.6, y: 1.6, yoyo: true, repeat: 3, duration: 0.25 })
    }
    const ring = at(arrowRing(3, COLORS.green), -6.5, 9, 0)
    g.add(ring)
    popIn(ring)
    spin(world, ring, 1, "z")
  },

  recycledplastics(world) {
    const g = stationGroup(world, "chem_plant")
    if (!g) return
    const ring = at(arrowRing(3.2), 0, 8, 0)
    g.add(ring)
    popIn(ring)
    spin(world, ring, 1.3, "z")
    cleanSmoke(world, "chem_plant", 0.5)
  },

  concrete(world) {
    const g = stationGroup(world, "quarry")
    if (!g) return
    for (const heapMesh of g.userData.heaps || []) {
      heapMesh.material = mat(COLORS.fieldGreen)
      gsap.fromTo(heapMesh.scale, { x: 1, y: 1, z: 1 }, { x: 1.25, y: 0.8, z: 1.25, yoyo: true, repeat: 1, duration: 0.3 })
    }
    cleanSmoke(world, "quarry", 0.7)
  },

  farm(world) {
    const g = stationGroup(world, "farm")
    if (!g) return
    for (const [i, field] of (g.userData.fields || []).entries()) {
      gsap.delayedCall(i * 0.25, () => (field.material = mat(COLORS.grassDark)))
    }
    for (let i = 0; i < 6; i++) {
      const hedge = at(box(0.8, 0.8, 0.8, COLORS.tree), -6 + i * 2.6, 0, 8.5)
      g.add(hedge)
      popIn(hedge, { delay: 0.4 + i * 0.1 })
    }
    cleanSmoke(world, "farm", 0.7)
  },

  greenit(world) {
    const g = stationGroup(world, "office_park")
    if (!g) return
    ;(g.userData.roofs || []).forEach(({ x, z, y }, i) => {
      for (let p = 0; p < 2; p++) {
        const panel = solarPanel()
        panel.position.set(x - 0.7 + p * 1.4, y + 0.2, z)
        g.add(panel)
        dropIn(panel, { delay: i * 0.2 + p * 0.1 })
      }
      const leaf = at(box(0.9, 0.9, 0.9, COLORS.green), x, y + 0.5, z + 1)
      g.add(leaf)
      popIn(leaf, { delay: 0.6 + i * 0.2 })
    })
  },

  train(world) {
    buildRailway(world)
    // Die Hälfte der Lkw verschwindet von der Straße
    const trucks = world.moversTagged("trucks")
    trucks.slice(0, Math.ceil(trucks.length / 2)).forEach((mover) => gsap.to(mover.object.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.5, onComplete: () => (mover.paused = true) }))
  },

  plane(world) {
    const [mover] = world.moversTagged("plane")
    const airport = stationGroup(world, "airport")
    if (mover && airport) {
      mover.paused = true
      const runway = airport.userData.runway
      const target = new THREE.Vector3(airport.position.x + runway.x, runway.y, airport.position.z + 4)
      gsap.to(mover.object.position, { x: target.x, y: target.y, z: target.z, duration: 2.4, ease: "power2.inOut" })
      // Flugzeug zeigt in +x; auf der Landebahn entlang z ausrichten
      gsap.to(mover.object.rotation, { x: 0, y: -Math.PI / 2, z: 0, duration: 2.4 })
    }
    const screen = new THREE.Group()
    screen.add(at(box(0.2, 3, 0.2, COLORS.steelDark), 0, 0, 0))
    screen.add(at(box(4, 2.4, 0.2, COLORS.navy600), 0, 3, 0))
    screen.add(at(box(3.6, 2, 0.1, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 0.5 }), 0, 3.2, 0.12))
    const target = airport || world.dynamic
    target.add(at(screen, 1.5, 0, 6.5, 0.3))
    popIn(screen, { delay: 1.8 })
    cleanSmoke(world, "airport", 1)
  },

  bikes(world) {
    world.moversTagged("commuters").forEach((mover, i) => {
      gsap.to(mover.object.scale, {
        x: 0.001, y: 0.001, z: 0.001, duration: 0.4, delay: i * 0.15,
        onComplete: () => {
          world.dynamic.remove(mover.object)
          const b = bike(i % 2 ? COLORS.lime : COLORS.green)
          b.scale.setScalar(1.6)
          world.dynamic.add(b)
          mover.object = b
          mover.speed = 3
          popIn(b)
        }
      })
    })
  },

  ecodesign(world) {
    const g = stationGroup(world, "customers")
    if (!g) return
    ;(g.userData.products || []).forEach((machine, i) => {
      gsap.delayedCall(i * 0.3, () => {
        machine.userData.light.material = mat(COLORS.green, { emissive: COLORS.green, emissiveIntensity: 0.9 })
      })
      gsap.fromTo(machine.scale, { x: 1, y: 1, z: 1 }, { x: 0.85, y: 0.85, z: 0.85, duration: 0.6, delay: i * 0.3, ease: "back.inOut(2)" })
    })
    cleanSmoke(world, "customers", 0.8)
  },

  circular(world) {
    const a = STATION_LAYOUT.customers
    const b = STATION_LAYOUT.recycling
    const ring = at(arrowRing(5), (a[0] + b[0]) / 2, 3, (a[1] + b[1]) / 2)
    world.dynamic.add(ring)
    popIn(ring)
    spin(world, ring, 0.9, "z")
    cleanSmoke(world, "recycling", 0.8)
  }
}

export function playAnimation(world, name) {
  const animation = ANIMATIONS[name]
  return Promise.resolve(animation ? animation(world) : null)
}
